import { useEffect, useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Check } from 'lucide-react';
import { toast } from 'sonner';
import { meApi } from '../../api/endpoints/me';
import { handleApiError } from '../../api/errorMapping';
import { Card, Button } from '@fiado/react-kit';
import { TextField } from '../ui/TextField';
import { LoadingButton } from '../ui/LoadingButton';
import { Banner } from '../ui/Banner';
import { z } from 'zod';
import { EMAIL_OTP_LENGTH, OTP_RESEND_COUNTDOWN_SECONDS, formatTime } from '../../lib/otp';
import { useMyTenantIdentifier } from '../../hooks/useMyTenantIdentifier';
import { PHONE_E164_REGEX } from '../../lib/identifier';

/**
 * F4.4 (DEC-RBAC-081): self-service para agregar + verificar el identificador SECUNDARIO (login dual).
 *
 * A diferencia de `MfaEmailSwitch` (que conoce el correo del usuario y auto-envía el OTP al montar),
 * aquí el secundario es un dato NUEVO que el usuario ingresa (el opuesto a su primario: email o
 * teléfono). Por eso el flujo es en dos fases:
 *   1. el usuario escribe el secundario → "Enviar código" (`addSecondaryIdentifier({ value })`),
 *      que dispara un OTP por el canal del secundario (email/SMS) + arranca el countdown de 2 min;
 *   2. ingresa el OTP → "Verificar" (`verifySecondaryIdentifier({ value, otpCode })`).
 *
 * El TIPO del secundario es SIEMPRE el opuesto al `principalIdentifier` del tenant
 * (`useMyTenantIdentifier`): principal PHONE → secundario EMAIL, principal EMAIL → secundario TELÉFONO.
 * Por eso el campo (label/placeholder/type/validación) y el texto se derivan de ahí en vez de ser
 * genéricos. Si `allowSecondary === false`, el tenant no admite secundario y el componente no renderiza.
 *
 * Usa la `Card` de `@fiado/react-kit` (full-width, igual que las otras secciones del perfil).
 *
 * Largo del OTP: reusa `EMAIL_OTP_LENGTH` (4) de `lib/otp`. Si el OTP por SMS usara otro largo, hay
 * que parametrizarlo cuando el backend lo confirme (TD).
 */
export function SecondaryIdentifierSwitch() {
    const qc = useQueryClient();
    const { principalIdentifier, allowSecondary, email, phone } = useMyTenantIdentifier();

    // El secundario es el OPUESTO al principal del tenant.
    const secondaryIsEmail = principalIdentifier === 'PHONE';

    // Valor ACTUAL del secundario en el perfil (write-on-verify ⇒ si está seteado, ya está verificado).
    // principal PHONE → secundario = email; principal EMAIL → secundario = teléfono.
    const currentSecondary = (secondaryIsEmail ? email : phone)?.trim() ?? '';
    const field = secondaryIsEmail
        ? {
              label: 'Correo electrónico',
              placeholder: 'correo@ejemplo.com',
              inputType: 'email' as const,
              description:
                  'Agrega tu correo como segundo dato para iniciar sesión (el opuesto a tu ' +
                  'identificador principal). Te enviaremos un código para verificarlo.',
              isValid: (v: string) => z.string().email().safeParse(v).success,
          }
        : {
              label: 'Teléfono',
              placeholder: '+52...',
              inputType: 'tel' as const,
              description:
                  'Agrega tu teléfono como segundo dato para iniciar sesión (el opuesto a tu ' +
                  'identificador principal). Te enviaremos un código para verificarlo.',
              isValid: (v: string) => PHONE_E164_REGEX.test(v),
          };

    const [value, setValue] = useState('');
    const [code, setCode] = useState('');
    const [sent, setSent] = useState(false);
    const [countdown, setCountdown] = useState(OTP_RESEND_COUNTDOWN_SECONDS);
    const [sendTick, setSendTick] = useState(0);
    const [error, setError] = useState<string | null>(null);
    const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

    const addMut = useMutation({
        mutationFn: () => meApi.addSecondaryIdentifier({ value: value.trim() }),
        onSuccess: () => {
            setSent(true);
            setCountdown(OTP_RESEND_COUNTDOWN_SECONDS);
            setSendTick((t) => t + 1);
        },
        onError: handleApiError,
    });

    // (Re)arrancar el countdown en cada envío exitoso (el reset a 120 lo hace onSuccess del add).
    useEffect(() => {
        if (sendTick === 0) return;
        intervalRef.current = setInterval(() => {
            setCountdown((prev) => (prev > 0 ? prev - 1 : 0));
        }, 1000);
        return () => {
            if (intervalRef.current) clearInterval(intervalRef.current);
        };
    }, [sendTick]);

    const verifyMut = useMutation({
        mutationFn: () => meApi.verifySecondaryIdentifier({ value: value.trim(), otpCode: code }),
        onSuccess: async () => {
            toast.success('Identificador secundario verificado');
            await qc.invalidateQueries({ queryKey: ['me', 'profile'] });
            setSent(false);
            setCode('');
            setValue('');
        },
        onError: (err) => {
            handleApiError(err);
            setError('Código inválido o expirado. Verifica e intenta de nuevo.');
        },
    });

    const canSend = field.isValid(value.trim()) && !addMut.isPending;
    const canResend = countdown === 0 && !addMut.isPending;

    // El tenant no admite identificador secundario → no mostramos la sección.
    if (!allowSecondary) return null;

    // Ya configurado (write-on-verify): mostramos el valor + "verificado", sin el form de agregar.
    if (currentSecondary) {
        const kindLabel = secondaryIsEmail ? 'correo' : 'teléfono';
        return (
            <Card
                title="Identificador secundario"
                description={`Tu identificador secundario (${kindLabel}) está configurado y verificado. Puedes usarlo para iniciar sesión.`}
            >
                <div className="flex items-center justify-between gap-4 rounded-md border border-border bg-muted/40 px-4 py-3">
                    <span className="font-medium break-all">{currentSecondary}</span>
                    <span className="inline-flex shrink-0 items-center gap-1 text-sm font-medium text-emerald-600">
                        <Check className="size-4" aria-hidden="true" />
                        Verificado
                    </span>
                </div>
            </Card>
        );
    }

    return (
        <Card title="Identificador secundario" description={field.description}>
            <div className="space-y-6">
                {error && (
                    <Banner
                        variant="error"
                        dismissible
                        onDismiss={() => {
                            setError(null);
                        }}
                    >
                        {error}
                    </Banner>
                )}

                {/* Fase 1: ingresar el secundario y disparar el OTP. */}
                <form
                    onSubmit={(e) => {
                        e.preventDefault();
                        setError(null);
                        if (canSend) addMut.mutate();
                    }}
                    className="space-y-4"
                    noValidate
                >
                    <TextField
                        label={field.label}
                        type={field.inputType}
                        autoComplete="off"
                        placeholder={field.placeholder}
                        value={value}
                        disabled={sent}
                        onChange={(e) => {
                            setValue(e.target.value);
                        }}
                    />
                    {!sent && (
                        <LoadingButton
                            type="submit"
                            className="w-full"
                            isLoading={addMut.isPending}
                            loadingText="Enviando…"
                            disabled={!canSend}
                        >
                            Enviar código
                        </LoadingButton>
                    )}
                </form>

                {/* Fase 2: ingresar el OTP recibido y verificar. */}
                {sent && (
                    <form
                        onSubmit={(e) => {
                            e.preventDefault();
                            setError(null);
                            verifyMut.mutate();
                        }}
                        className="space-y-4"
                        noValidate
                    >
                        <TextField
                            label="Código de verificación"
                            inputMode="numeric"
                            autoComplete="one-time-code"
                            maxLength={EMAIL_OTP_LENGTH}
                            placeholder={'•'.repeat(EMAIL_OTP_LENGTH)}
                            value={code}
                            onChange={(e) => {
                                setCode(e.target.value.replace(/\D/g, ''));
                            }}
                        />
                        <LoadingButton
                            type="submit"
                            className="w-full"
                            isLoading={verifyMut.isPending}
                            loadingText="Verificando…"
                            disabled={code.length !== EMAIL_OTP_LENGTH}
                        >
                            Verificar y activar
                        </LoadingButton>

                        <div className="pt-2 text-center text-sm space-y-2">
                            {countdown > 0 ? (
                                <p className="text-muted-foreground">
                                    Puedes pedir un código nuevo en{' '}
                                    <span className="font-mono font-semibold">
                                        {formatTime(countdown)}
                                    </span>
                                </p>
                            ) : (
                                <p className="text-muted-foreground">¿No te llegó el código?</p>
                            )}
                            <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                disabled={!canResend}
                                onClick={() => {
                                    setError(null);
                                    addMut.mutate();
                                }}
                            >
                                Reenviar código
                            </Button>
                        </div>
                    </form>
                )}
            </div>
        </Card>
    );
}
